import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import { arrayOf, func, node, number, object, shape, string } from 'prop-types';
import differenceBy from 'lodash/differenceBy';
import isEqual from 'lodash/isEqual';
import classNames from 'classnames';

import { types as sdkTypes } from '../../../util/sdkLoader';
import { parse } from '../../../util/urlHelpers';
import { propTypes } from '../../../util/types';
import { ensureListing } from '../../../util/data';
import { sdkBoundsToFixedCoordinates, hasSameSDKBounds } from '../../../util/maps';

import SearchMapPriceLabel from '../SearchMapPriceLabel/SearchMapPriceLabel';
import SearchMapInfoCard from '../SearchMapInfoCard/SearchMapInfoCard';
import SearchMapGroupLabel from '../SearchMapGroupLabel/SearchMapGroupLabel';
import {
  groupedByCoordinates,
  reducedToArray,
  adjustInfoCardVisibility,
  isMobileViewport,
} from './SearchMap.helpers';
import css from './SearchMapWithMapbox.module.css';

export const LABEL_HANDLE = 'SearchMapLabel';
export const INFO_CARD_HANDLE = 'SearchMapInfoCard';
export const SOURCE_AUTOCOMPLETE = 'autocomplete';
const BOUNDS_FIXED_PRECISION = 8;

const { LatLng: SDKLatLng, LatLngBounds: SDKLatLngBounds } = sdkTypes;

/**
 * Fit part of map (descriped with bounds) to visible map-viewport
 *
 * @param {Object} map - map that needs to be centered with given bounds
 * @param {SDK.LatLngBounds} bounds - the area that needs to be visible when map loads.
 */
export const fitMapToBounds = (map, bounds, options) => {
  const { padding = 0, isAutocompleteSearch = false } = options;

  // map bounds as string literal for google.maps
  const mapBounds = sdkBoundsToMapboxBounds(bounds);
  const paddingOptionMaybe = padding == null ? { padding } : {};
  const eventData = isAutocompleteSearch ? { searchSource: SOURCE_AUTOCOMPLETE } : {};

  // If bounds are given, use it (defaults to center & zoom).
  if (map && mapBounds) {
    map.fitBounds(mapBounds, { ...paddingOptionMaybe, linear: true, duration: 0 }, eventData);
  }
};

/**
 * Convert Mapbox formatted LatLng object to Sharetribe SDK's LatLng coordinate format
 * Longitudes > 180 and < -180 are converted to the correct corresponding value
 * between -180 and 180.
 *
 * @param {LngLat} mapboxLngLat - Mapbox LngLat
 *
 * @return {SDKLatLng} - Converted latLng coordinate
 */
export const mapboxLngLatToSDKLatLng = lngLat => {
  const mapboxLng = lngLat.lng;

  // For bounding boxes that overlap the antimeridian Mapbox sometimes gives
  // longitude values outside -180 and 180 degrees.Those values are converted
  // so that longitude is always between -180 and 180.
  const lng = mapboxLng > 180 ? mapboxLng - 360 : mapboxLng < -180 ? mapboxLng + 360 : mapboxLng;

  return new SDKLatLng(lngLat.lat, lng);
};

/**
 * Convert Mapbox formatted bounds object to Sharetribe SDK's bounds format
 *
 * @param {LngLatBounds} mapboxBounds - Mapbox LngLatBounds
 *
 * @return {SDKLatLngBounds} - Converted bounds
 */
export const mapboxBoundsToSDKBounds = mapboxBounds => {
  if (!mapboxBounds) {
    return null;
  }

  const ne = mapboxBounds.getNorthEast();
  const sw = mapboxBounds.getSouthWest();
  return new SDKLatLngBounds(mapboxLngLatToSDKLatLng(ne), mapboxLngLatToSDKLatLng(sw));
};

/**
 * Convert sdk bounds that overlap the antimeridian into values that can
 * be passed to Mapbox. This is achieved by converting the SW longitude into
 * a value less than -180 that flows over the antimeridian.
 *
 * @param {SDKLatLng} bounds - bounds passed to the map
 *
 * @return {LngLatBoundsLike} a bounding box that is compatible with Mapbox
 */
const sdkBoundsToMapboxBounds = bounds => {
  if (!bounds) {
    return null;
  }
  const { ne, sw } = bounds;

  // if sw lng is > ne lng => the bounds overlap antimeridian
  // => flip the nw lng to the negative side so that the value
  // is less than -180
  const swLng = sw.lng > ne.lng ? -360 + sw.lng : sw.lng;

  return [
    [swLng, sw.lat],
    [ne.lng, ne.lat],
  ];
};

/**
 * Return map bounds as SDKBounds
 *
 * @param {Mapbox} map - Mapbox map from where the bounds are asked
 *
 * @return {SDKLatLngBounds} - Converted bounds of given map
 */
export const getMapBounds = map => mapboxBoundsToSDKBounds(map.getBounds());

/**
 * Return map center as SDKLatLng
 *
 * @param {Mapbox} map - Mapbox map from where the center is asked
 *
 * @return {SDKLatLng} - Converted center of given map
 */
export const getMapCenter = map => mapboxLngLatToSDKLatLng(map.getCenter());

/**
 * Check if map library is loaded
 */
export const isMapsLibLoaded = () =>
  typeof window !== 'undefined' && window.mapboxgl && window.mapboxgl;

/**
 * Return price labels grouped by listing locations.
 * This is a helper function for SearchMapWithMapbox component.
 */
const priceLabelsInLocations = (
  listings,
  activeListingId,
  infoCardOpen,
  onListingClicked,
  mapComponentRefreshToken,
  onLabelInteractionStart,
  onLabelInteractionEnd
) => {
  const listingArraysInLocations = reducedToArray(groupedByCoordinates(listings));
  const priceLabels = listingArraysInLocations.reverse().map(listingArr => {
    const isActive = activeListingId
      ? !!listingArr.find(l => activeListingId.uuid === l.id.uuid)
      : false;

    // If location contains only one listing, print price label
    if (listingArr.length === 1) {
      const listing = listingArr[0];
      const infoCardOpenIds = Array.isArray(infoCardOpen)
        ? infoCardOpen.map(l => l.id.uuid)
        : infoCardOpen
        ? [infoCardOpen.id.uuid]
        : [];

      // On desktop, hide the pin label while its card is open. On mobile keep it so
      // ghost clicks and re-taps still hit a marker instead of the bare map.
      if (infoCardOpen != null && infoCardOpenIds.includes(listing.id.uuid) && !isMobileViewport()) {
        return null;
      }

      // Explicit type change to object literal for Google OverlayViews (geolocation is SDK type)
      const { geolocation } = listing.attributes;

      const key = listing.id.uuid;
      return {
        markerId: `price_${key}`,
        location: geolocation,
        type: 'price',
        componentProps: {
          key,
          isActive,
          className: LABEL_HANDLE,
          listing,
          onListingClicked,
          mapComponentRefreshToken,
          onLabelInteractionStart,
          onLabelInteractionEnd,
        },
      };
    }

    // Explicit type change to object literal for Google OverlayViews (geolocation is SDK type)
    const firstListing = ensureListing(listingArr[0]);
    const geolocation = firstListing.attributes.geolocation;

    const key = listingArr[0].id.uuid;
    return {
      markerId: `group_${key}`,
      location: geolocation,
      type: 'group',
      componentProps: {
        key,
        isActive,
        className: LABEL_HANDLE,
        listings: listingArr,
        onListingClicked,
        mapComponentRefreshToken,
        onLabelInteractionStart,
        onLabelInteractionEnd,
      },
    };
  });
  return priceLabels;
};

/**
 * Return info card. This is a helper function for SearchMapWithMapbox component.
 */
const infoCardComponent = (
  infoCardOpen,
  onListingInfoCardClicked,
  createURLToListing,
  mapComponentRefreshToken
) => {
  const listingsArray = Array.isArray(infoCardOpen) ? infoCardOpen : [infoCardOpen];

  if (!infoCardOpen) {
    return null;
  }

  const firstListing = ensureListing(listingsArray[0]);
  const key = firstListing.id.uuid;
  const geolocation = firstListing.attributes.geolocation;

  return {
    markerId: `infoCard_${key}`,
    location: geolocation,
    componentProps: {
      key,
      mapComponentRefreshToken,
      className: INFO_CARD_HANDLE,
      listings: listingsArray,
      onListingInfoCardClicked,
      createURLToListing,
    },
  };
};

/**
 * SearchMap component using Mapbox as map provider
 */
class SearchMapWithMapbox extends Component {
  constructor(props) {
    super(props);
    this.map = typeof window !== 'undefined' && window.mapboxMap ? window.mapboxMap : null;
    this.currentMarkers = [];
    this.currentInfoCard = null;
    this.hasInfoCardResizeListener = false;
    this.dragPanDisabledForLabel = false;
    this.state = { mapContainer: null, isMapReady: false };
    this.viewportBounds = null;

    this.onMount = this.onMount.bind(this);
    this.onMoveend = this.onMoveend.bind(this);
    this.initializeMap = this.initializeMap.bind(this);
    this.handleDoubleClickOnInfoCard = this.handleDoubleClickOnInfoCard.bind(this);
    this.handleMobilePinchZoom = this.handleMobilePinchZoom.bind(this);
    this.adjustInfoCardPosition = this.adjustInfoCardPosition.bind(this);
    this.handleInfoCardResize = this.handleInfoCardResize.bind(this);
    this.removeInfoCardMarker = this.removeInfoCardMarker.bind(this);
    this.stopMapInteraction = this.stopMapInteraction.bind(this);
    this.attachLabelInteractionListeners = this.attachLabelInteractionListeners.bind(this);
    this.handleLabelInteractionStart = this.handleLabelInteractionStart.bind(this);
    this.handleLabelInteractionEnd = this.handleLabelInteractionEnd.bind(this);
  }

  handleLabelInteractionStart() {
    if (this.map?.dragPan?.disable) {
      this.map.dragPan.disable();
      this.dragPanDisabledForLabel = true;
    }
  }

  handleLabelInteractionEnd() {
    if (this.dragPanDisabledForLabel && this.map?.dragPan?.enable) {
      this.map.dragPan.enable();
      this.dragPanDisabledForLabel = false;
    }
  }

  attachLabelInteractionListeners(markerContainer) {
    if (!markerContainer || markerContainer.dataset.mapInteractionBound) {
      return;
    }

    markerContainer.dataset.mapInteractionBound = 'true';

    // Fast-tap detection on the marker container itself. Attaching this on
    // capture (before stopMapInteraction below) lets us catch the tap even
    // though we still need to block the touch from reaching Mapbox to avoid
    // triggering a pan. We open the listing card at `touchend` and call
    // `preventDefault()` there to suppress the ~300ms synthesized click and
    // any double activation via the inner button's React onClick. This is
    // what makes the pin/tag open the card on the first tap, immediately.
    //
    // The listing/callback is read from `markerContainer.__markerData` at
    // event time (populated in render, see below) so a reused marker always
    // resolves to the correct listing without re-attaching listeners.
    let touchStartCoords = null;
    const onTouchStart = e => {
      const touch = e.touches && e.touches[0];
      if (touch) {
        touchStartCoords = { x: touch.clientX, y: touch.clientY };
      }
    };
    const onTouchEnd = e => {
      const touch = e.changedTouches && e.changedTouches[0];
      const start = touchStartCoords;
      touchStartCoords = null;
      if (!touch || !start) return;
      if (Math.hypot(touch.clientX - start.x, touch.clientY - start.y) > 10) return;
      const data = markerContainer.__markerData;
      if (!data || !data.onListingClicked) return;
      // Prevent the browser from firing a delayed synthesized click on top
      // of our activation (which the button's onClick would otherwise run).
      e.preventDefault();
      data.onListingClicked(data.listingOrListings);
    };
    const onTouchCancel = () => {
      touchStartCoords = null;
    };

    markerContainer.addEventListener('touchstart', onTouchStart, {
      capture: true,
      passive: true,
    });
    markerContainer.addEventListener('touchend', onTouchEnd, {
      capture: true,
      passive: false,
    });
    markerContainer.addEventListener('touchcancel', onTouchCancel, {
      capture: true,
      passive: true,
    });

    // Existing behaviour: stop Mapbox from panning/zooming when the user
    // interacts with a marker. `touchstart` must be registered AFTER
    // `onTouchStart` above so our coord-tracker runs first before this
    // handler calls stopPropagation and short-circuits the rest of the
    // dispatch path.
    markerContainer.addEventListener('mousedown', this.stopMapInteraction, true);
    markerContainer.addEventListener('touchstart', this.stopMapInteraction, {
      capture: true,
      passive: false,
    });
    markerContainer.addEventListener('dblclick', this.stopMapInteraction, true);
  }

  handleMobilePinchZoom(e) {
    e.preventDefault();
    // A hack to prevent pinch zoom gesture in mobile Safari.
    // Setting `document.body.style.zoom` forces a full-page layout
    // recalculation, and gesturechange fires ~60x/sec during a pinch, so we
    // only apply the fallback zoom fix ONCE per gesture (on gesturestart)
    // instead of thrashing layout on every intermediate event.
    if (e.type !== 'gesturestart') return;
    if (typeof window !== 'undefined' && window?.document) {
      document.body.style.zoom = 0.99;
    }
  }

  componentDidMount() {
    // Gesture listeners are also (re)attached from `onMount` — leaving both
    // registrations would double-fire the handler on every gesture and
    // double the layout cost. onMount is the reliable point (it runs when
    // the actual map DOM element is available), so we skip attaching here.
  }

  componentDidUpdate(prevProps) {
    if (!isEqual(prevProps.location, this.props.location)) {
      // If no mapSearch url parameter is given, this is original location search
      const { mapSearch } = parse(this.props.location.search, {
        latlng: ['origin'],
        latlngBounds: ['bounds'],
      });
      if (!mapSearch) {
        this.viewportBounds = null;
      } else if (!isEqual(prevProps.bounds, this.props.bounds)) {
        // New search from Travel Destinations / topbar - bounds changed, fit map to new destination
        this.viewportBounds = null;
      }
    }

    if (this.map) {
      const currentBounds = getMapBounds(this.map);

      // Do not call fitMapToBounds if bounds are the same.
      // Our bounds are viewport bounds, and fitBounds will try to add margins around those bounds
      // that would result to zoom-loop (bound change -> fitmap -> bounds change -> ...)
      if (!isEqual(this.props.bounds, currentBounds) && !this.viewportBounds) {
        fitMapToBounds(this.map, this.props.bounds, { padding: 0, isAutocompleteSearch: true });
      }
    }

    if (!this.map && this.state.mapContainer) {
      this.initializeMap();

      /* Notify parent component that Mapbox map is loaded */
      this.props.onMapLoad(this.map);
    } else if (prevProps.mapComponentRefreshToken !== this.props.mapComponentRefreshToken) {
      /* Notify parent component that Mapbox map is loaded */
      this.props.onMapLoad(this.map);
    }

    if (
      this.map &&
      this.currentInfoCard &&
      !this.currentInfoCard.isMobileOverlay &&
      (prevProps.infoCardOpen !== this.props.infoCardOpen ||
        prevProps.mapComponentRefreshToken !== this.props.mapComponentRefreshToken)
    ) {
      this.scheduleInfoCardPositionAdjustment();
    }
  }

  scheduleInfoCardPositionAdjustment() {
    if (typeof window === 'undefined') {
      return;
    }

    window.requestAnimationFrame(() => {
      this.adjustInfoCardPosition();
      // Re-measure after images load and change card height.
      window.setTimeout(() => this.adjustInfoCardPosition(), 350);
    });
  }

  adjustInfoCardPosition() {
    if (!this.map || !this.currentInfoCard || this.currentInfoCard.isMobileOverlay) {
      return;
    }

    const { marker, markerContainer } = this.currentInfoCard;
    adjustInfoCardVisibility(this.map, marker, markerContainer);
  }

  ensureInfoCardResizeListener() {
    if (!this.hasInfoCardResizeListener) {
      window.addEventListener('resize', this.handleInfoCardResize);
      this.hasInfoCardResizeListener = true;
    }
  }

  handleInfoCardResize() {
    if (!this.currentInfoCard) {
      return;
    }

    if (isMobileViewport()) {
      // Re-render so the card switches between overlay and marker modes.
      this.forceUpdate();
      return;
    }

    if (!this.currentInfoCard.isMobileOverlay) {
      this.scheduleInfoCardPositionAdjustment();
    }
  }

  removeInfoCardMarker() {
    if (!this.currentInfoCard) {
      return;
    }

    window.removeEventListener('resize', this.handleInfoCardResize);
    this.hasInfoCardResizeListener = false;

    const { markerContainer, marker } = this.currentInfoCard;

    if (markerContainer) {
      markerContainer.removeEventListener('dblclick', this.handleDoubleClickOnInfoCard);
      markerContainer.removeEventListener('mousedown', this.stopMapInteraction);
      markerContainer.removeEventListener('touchstart', this.stopMapInteraction);
    }

    if (marker) {
      marker.remove();
    }

    this.currentInfoCard = null;
  }

  componentWillUnmount() {
    if (this.currentInfoCard) {
      this.removeInfoCardMarker();
    }
    if (this.dragPanDisabledForLabel && this.map?.dragPan?.enable) {
      this.map.dragPan.enable();
      this.dragPanDisabledForLabel = false;
    }
    document.removeEventListener('gesturestart', this.handleMobilePinchZoom, false);
    document.removeEventListener('gesturechange', this.handleMobilePinchZoom, false);
    document.removeEventListener('gestureend', this.handleMobilePinchZoom, false);
    this.gestureListenersAttached = false;
  }

  onMount(element) {
    // React ref callbacks may fire more than once across mounts/updates,
    // and this component gets re-created a lot on the search page. Guard
    // so we don't stack duplicate gesture listeners (which would multiply
    // the (already expensive) `handleMobilePinchZoom` cost on every pinch).
    if (!this.gestureListenersAttached && element) {
      document.addEventListener('gesturestart', this.handleMobilePinchZoom, false);
      document.addEventListener('gesturechange', this.handleMobilePinchZoom, false);
      document.addEventListener('gestureend', this.handleMobilePinchZoom, false);
      this.gestureListenersAttached = true;
    }

    this.setState({ mapContainer: element });
  }

  onMoveend(e) {
    if (this.map) {
      // If reusableMapHiddenHandle is given and parent element has that class,
      // we don't listen moveend events.
      // This fixes mobile Chrome bug that sends map events to invisible map components.
      const isHiddenByReusableMap =
        this.props.reusableMapHiddenHandle &&
        this.state.mapContainer.parentElement.classList.contains(
          this.props.reusableMapHiddenHandle
        );
      if (!isHiddenByReusableMap) {
        const viewportMapBounds = getMapBounds(this.map);
        const viewportMapCenter = getMapCenter(this.map);
        const viewportBounds = sdkBoundsToFixedCoordinates(
          viewportMapBounds,
          BOUNDS_FIXED_PRECISION
        );

        // ViewportBounds from (previous) rendering differ from viewportBounds currently set to map
        // I.e. user has changed the map somehow: moved, panned, zoomed, resized
        const viewportBoundsChanged =
          this.viewportBounds && !hasSameSDKBounds(this.viewportBounds, viewportBounds);

        this.props.onMapMoveEnd(viewportBoundsChanged, {
          viewportBounds,
          viewportMapCenter,
          isUserInitiated: !!(e && e.originalEvent),
        });
        this.viewportBounds = viewportBounds;
      }

      if (this.currentInfoCard && !this.currentInfoCard.isMobileOverlay) {
        this.scheduleInfoCardPositionAdjustment();
      }
    }
  }

  initializeMap() {
    const { offsetHeight, offsetWidth } = this.state.mapContainer;
    const hasDimensions = offsetHeight > 0 && offsetWidth > 0;
    if (hasDimensions) {
      this.map = new window.mapboxgl.Map({
        container: this.state.mapContainer,
        style: 'mapbox://styles/mapbox/streets-v10',
        scrollZoom: true,
      });
      window.mapboxMap = this.map;

      var nav = new window.mapboxgl.NavigationControl({ showCompass: false });
      this.map.addControl(nav, 'top-left');

      this.map.on('moveend', this.onMoveend);

      // Introduce rerendering after map is ready (to include labels),
      // but keep the map out of state life cycle.
      this.setState({ isMapReady: true });
    }
  }

  handleDoubleClickOnInfoCard(e) {
    e.stopPropagation();
  }

  stopMapInteraction(e) {
    e.stopPropagation();
  }

  render() {
    const {
      id,
      className,
      listings,
      activeListingId,
      infoCardOpen,
      onListingClicked,
      onListingInfoCardClicked,
      createURLToListing,
      mapComponentRefreshToken,
      config,
    } = this.props;

    if (this.map) {
      // Create markers out of price labels and grouped labels
      const labels = priceLabelsInLocations(
        listings,
        activeListingId,
        infoCardOpen,
        onListingClicked,
        mapComponentRefreshToken,
        this.handleLabelInteractionStart,
        this.handleLabelInteractionEnd
      );

      // If map has moved or info card opened, unnecessary markers need to be removed
      const removableMarkers = differenceBy(this.currentMarkers, labels, 'markerId');
      removableMarkers.forEach(rm => rm.marker.remove());

      // Helper function to create markers to given container
      const createMarker = (data, markerContainer) =>
        new window.mapboxgl.Marker(markerContainer, { anchor: 'bottom' })
          .setLngLat([data.location.lng, data.location.lat])
          .addTo(this.map);

      // SearchMapPriceLabel and SearchMapGroupLabel:
      // create a new marker or use existing one if markerId is among previously rendered markers
      this.currentMarkers = labels
        .filter(v => v != null)
        .map(m => {
          const existingMarkerId = this.currentMarkers.findIndex(
            marker => m.markerId === marker.markerId && marker.marker
          );

          // Keep the latest listing + callback reachable from the native
          // tap handler on the marker container. `onListingClicked` here is
          // stable (bound in SearchMap constructor), but the listing object
          // can be refreshed between renders — reading it fresh at event
          // time avoids opening the card with a stale listing.
          const listingOrListings =
            m.type === 'group' ? m.componentProps.listings : m.componentProps.listing;

          if (existingMarkerId >= 0) {
            const { marker, markerContainer, ...rest } = this.currentMarkers[existingMarkerId];
            markerContainer.__markerData = {
              onListingClicked: m.componentProps.onListingClicked,
              listingOrListings,
            };
            this.attachLabelInteractionListeners(markerContainer);
            return { ...rest, ...m, markerContainer, marker };
          } else {
            const markerContainer = document.createElement('div');
            markerContainer.setAttribute('id', m.markerId);
            markerContainer.classList.add(css.labelContainer);
            markerContainer.__markerData = {
              onListingClicked: m.componentProps.onListingClicked,
              listingOrListings,
            };
            this.attachLabelInteractionListeners(markerContainer);
            const marker = createMarker(m, markerContainer);
            return { ...m, markerContainer, marker };
          }
        });

      /* Create marker for SearchMapInfoCard component */
      if (infoCardOpen) {
        const infoCard = infoCardComponent(
          infoCardOpen,
          onListingInfoCardClicked,
          createURLToListing,
          mapComponentRefreshToken
        );

        const isSameInfoCard =
          this.currentInfoCard && this.currentInfoCard.markerId === infoCard.markerId;
        const useMobileOverlay = isMobileViewport();

        if (useMobileOverlay) {
          if (!isSameInfoCard) {
            this.removeInfoCardMarker();
          }

          this.currentInfoCard = {
            ...(isSameInfoCard ? this.currentInfoCard : {}),
            ...infoCard,
            isMobileOverlay: true,
            markerContainer: null,
            marker: null,
          };

          this.ensureInfoCardResizeListener();
        } else {
          if (this.currentInfoCard?.isMobileOverlay) {
            this.removeInfoCardMarker();
          }

          if (isSameInfoCard && this.currentInfoCard?.marker) {
            this.currentInfoCard = {
              ...this.currentInfoCard,
              ...infoCard,
              isMobileOverlay: false,
            };
            this.scheduleInfoCardPositionAdjustment();
          } else {
            this.removeInfoCardMarker();

            const infoCardContainer = document.createElement('div');
            infoCardContainer.setAttribute('id', infoCard.markerId);
            infoCardContainer.classList.add(css.infoCardContainer);
            infoCardContainer.addEventListener('dblclick', this.handleDoubleClickOnInfoCard, false);
            infoCardContainer.addEventListener('mousedown', this.stopMapInteraction, false);
            infoCardContainer.addEventListener('touchstart', this.stopMapInteraction, false);

            this.currentInfoCard = {
              ...infoCard,
              isMobileOverlay: false,
              markerContainer: infoCardContainer,
              marker: createMarker(infoCard, infoCardContainer),
            };

            this.ensureInfoCardResizeListener();
            this.scheduleInfoCardPositionAdjustment();
          }
        }
      } else {
        this.removeInfoCardMarker();
      }
    }

    return (
      <div
        id={id}
        ref={this.onMount}
        className={classNames(className, css.fullArea)}
        onClick={this.props.onClick}
      >
        {this.currentMarkers.map(m => {
           if(typeof window !== 'undefined' && window?.document){
          // Remove existing activeLabel classes and add it only to the correct container
          m.markerContainer.classList.remove(css.activeLabel);
          if (activeListingId && activeListingId.uuid === m.componentProps.key) {
            m.markerContainer.classList.add(css.activeLabel);
          }

          const isMapReadyForMarkers = this.map && m.markerContainer;
          // DOM node that should be used as portal's root
          const portalDOMContainer = isMapReadyForMarkers
            ? document.getElementById(m.markerContainer.id)
            : null;

          // Create component portals for correct marker containers
          if (isMapReadyForMarkers && m.type === 'price') {
            return ReactDOM.createPortal(
              <SearchMapPriceLabel {...m.componentProps} config={config} />,
              portalDOMContainer
            );
          } else if (isMapReadyForMarkers && m.type === 'group') {
            return ReactDOM.createPortal(
              <SearchMapGroupLabel {...m.componentProps} />,
              portalDOMContainer
            );
          }
          }
          return null;
        })}
        {this.state.mapContainer && this.currentInfoCard
          ? this.currentInfoCard.isMobileOverlay
            ? ReactDOM.createPortal(
                <div className={css.infoCardOverlay}>
                  <SearchMapInfoCard {...this.currentInfoCard.componentProps} config={config} />
                </div>,
                this.state.mapContainer
              )
            : ReactDOM.createPortal(
                <SearchMapInfoCard {...this.currentInfoCard.componentProps} config={config} />,
                this.currentInfoCard.markerContainer
              )
          : null}
      </div>
    );
  }
}

SearchMapWithMapbox.defaultProps = {
  id: 'map',
  center: null,
  priceLabels: [],
  infoCard: null,
  zoom: 11,
  reusableMapHiddenHandle: null,
  scrollZoom: true, // Enable scroll zoom
};

SearchMapWithMapbox.propTypes = {
  id: string,
  center: propTypes.latlng,
  location: shape({
    search: string.isRequired,
  }).isRequired,
  priceLabels: arrayOf(node),
  infoCard: node,
  onClick: func.isRequired,
  onMapMoveEnd: func.isRequired,
  onMapLoad: func.isRequired,
  zoom: number,
  reusableMapHiddenHandle: string,
  config: object.isRequired,
};

export default SearchMapWithMapbox;
